var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { conferenceLeft, conferenceWillLeave, createConference } from '../base/conference/actions';
import { CONFERENCE_LEAVE_REASONS } from '../base/conference/constants';
import { getCurrentConference } from '../base/conference/functions';
import { setAudioMuted, setVideoMuted } from '../base/media/actions';
import { MEDIA_TYPE } from '../base/media/constants';
import { setIsSwitchingBreakoutRoom, setIsSwitchingToMainRoom } from '../base/mtApi';
import { isLocalParticipantModerator } from '../base/participants/functions';
import { createDesiredLocalTracks } from '../base/tracks/actions';
import { getLocalTracks, isLocalTrackMuted } from '../base/tracks/functions';
import { NOTIFICATION_TIMEOUT_TYPE, showNotification } from '../notifications';
import { clearNotifications } from '../notifications/actions';
import { SET_AUDIENCE_LAYOUT, SET_BACKSTAGE_ROSTER, SET_JOIN_HIDDEN, SET_LOCAL_RECORDING_ALLOWED, SET_MAIN_ROOM_ROSTER, SET_MEDIA_BLOCK_ALL, SET_ROOM_SWITCH_TARGET, SET_START_BACKSTAGE } from './actionTypes';
import { LOCAL_RECORDING_METADATA_KEY, MEDIA_BLOCK_ALL_METADATA_KEY, WEBINAR_AUDIENCE_LAYOUT, WEBINAR_AUDIENCE_LAYOUT_METADATA_KEY, WEBINAR_AUDIENCE_LAYOUT_REQUEST, WEBINAR_BACKSTAGE_ROSTER_REQUEST, WEBINAR_BRING_ALL_LIVE, WEBINAR_HIDDEN_REQUEST, WEBINAR_BLOCK_ALL_REQUEST, WEBINAR_KICK_REQUEST, WEBINAR_LOCAL_RECORDING_REQUEST, WEBINAR_MAIN_ROSTER_REQUEST, WEBINAR_MOVE_REQUEST, WEBINAR_MUTE_REQUEST, WEBINAR_ROLE_REQUEST } from './constants';
import { amIHeldBackstage, amIInBackstage, amIWebinarModerator, getAudienceLayout, getBackstageRoomName, getMainRoomName, isBackstageRoomName } from './functions';
import logger from './logger';
/**
 * Records the moderator's prejoin choice to start the meeting in backstage. It
 * is applied once the local user is confirmed moderator after joining.
 *
 * @param {boolean} enabled - Whether to start in backstage.
 * @returns {{
 *     type: SET_START_BACKSTAGE,
 *     enabled: boolean
 * }}
 */
export function setStartBackstage(enabled) {
    return {
        type: SET_START_BACKSTAGE,
        enabled
    };
}
/**
 * Records the moderator's prejoin choice to join hidden. The choice rides the
 * join presence (see WEBINAR_HIDDEN_REQUEST_PROPERTY) so the moderator is
 * already hidden in the first presence the audience receives.
 *
 * @param {?string} audience - Comma-separated roles that may still see the
 * local participant, or null/undefined to join normally. Prosody always keeps
 * moderator in the audience whatever is passed.
 * @returns {{
 *     type: SET_JOIN_HIDDEN,
 *     audience: ?string
 * }}
 */
export function setJoinHidden(audience) {
    return {
        type: SET_JOIN_HIDDEN,
        audience: audience || undefined
    };
}
/**
 * Stores a synchronized audience layout for a room.
 *
 * @param {string} roomName - The main webinar room name.
 * @param {IAudienceLayout} layout - The audience layout values.
 * @returns {Object}
 */
export function setAudienceLayout(roomName, layout) {
    return Object.assign({ type: SET_AUDIENCE_LAYOUT, roomName }, layout);
}
/**
 * Pushes the visitor-facing layout to every viewer: groupchat for people already
 * in the meeting, and room metadata so a vnode occupant who missed that message
 * still sees the current value at join.
 *
 * @param {Object} state - The redux state.
 * @param {IAudienceLayout} layout - The layout to publish.
 * @param {boolean} persist - Whether to also write room metadata.
 * @returns {void}
 */
function _publishAudienceLayout(state, layout, persist) {
    var _a, _b, _c;
    const conference = getCurrentConference(state);
    if (!conference) {
        return;
    }
    try {
        conference.sendMessage(Object.assign({ name: WEBINAR_AUDIENCE_LAYOUT }, layout));
    }
    catch (error) {
        logger.warn('Failed to announce webinar audience layout', error);
    }
    if (!persist) {
        return;
    }
    const metadataHandler = (_a = conference.getMetadataHandler) === null || _a === void 0 ? void 0 : _a.call(conference);
    if (!((_b = metadataHandler === null || metadataHandler === void 0 ? void 0 : metadataHandler.isSupported) === null || _b === void 0 ? void 0 : _b.call(metadataHandler)) || !((_c = conference.isModerator) === null || _c === void 0 ? void 0 : _c.call(conference))) {
        return;
    }
    try {
        metadataHandler.setMetadata(WEBINAR_AUDIENCE_LAYOUT_METADATA_KEY, layout);
    }
    catch (error) {
        logger.warn('Failed to persist webinar audience layout', error);
    }
}
/**
 * Broadcasts the moderator's current visitor-facing layout.
 *
 * @returns {Function}
 */
export function announceAudienceLayout() {
    return (_dispatch, getState) => {
        const state = getState();
        if (!isLocalParticipantModerator(state) || amIInBackstage(state)) {
            return;
        }
        const roomName = getMainRoomName(state);
        const conference = getCurrentConference(state);
        if (!roomName || !conference) {
            return;
        }
        _publishAudienceLayout(state, getAudienceLayout(state), false);
    };
}
/**
 * Updates and broadcasts the moderator-selected visitor-facing layout.
 *
 * @param {Partial<IAudienceLayout>} changes - Layout fields to update.
 * @returns {Function}
 */
export function updateAudienceLayout(changes) {
    return (dispatch, getState) => {
        const state = getState();
        if (!isLocalParticipantModerator(state) || amIInBackstage(state)) {
            return;
        }
        const roomName = getMainRoomName(state);
        if (!roomName) {
            return;
        }
        const layout = Object.assign(Object.assign({}, getAudienceLayout(state)), changes);
        dispatch(setAudienceLayout(roomName, layout));
        _publishAudienceLayout(getState(), layout, true);
    };
}
/**
 * Requests the active moderator's audience layout after a viewer joins.
 *
 * @returns {Function}
 */
export function requestAudienceLayout() {
    return (_dispatch, getState) => {
        var _a;
        try {
            (_a = getCurrentConference(getState())) === null || _a === void 0 ? void 0 : _a.sendMessage({
                name: WEBINAR_AUDIENCE_LAYOUT_REQUEST
            });
        }
        catch (error) {
            logger.warn('Failed to request webinar audience layout', error);
        }
    };
}
/**
 * Records the room a backstage/main switch is heading for, so the room-switch
 * screen can name it. Cleared once the join lands or fails.
 *
 * @param {string} [target] - 'backstage', 'main', or undefined to clear.
 * @returns {{
 *     type: SET_ROOM_SWITCH_TARGET,
 *     target: string | undefined
 * }}
 */
export function setRoomSwitchTarget(target, fromRoom) {
    return {
        type: SET_ROOM_SWITCH_TARGET,
        target,
        fromRoom
    };
}
/**
 * Puts the local participant back in the room they were in before a room switch
 * that the server refused. By then they have already left it and no conference
 * object remains to derive the name from, which is why the origin room is
 * carried in the switch state.
 *
 * Deliberately does not go through switchToRoom: this must not re-arm the
 * switch state, or a rejoin that fails again would bounce forever.
 *
 * @param {string} roomName - The room to rejoin.
 * @returns {Function}
 */
export function rejoinAfterFailedSwitch(roomName) {
    return (dispatch, getState) => {
        if (!roomName) {
            return;
        }
        // Mobile has no APP: the conference is (re)created straight from the
        // redux connection, the way its breakout-room moves do it.
        if (typeof APP === 'undefined') {
            dispatch(createConference(roomName));
            return;
        }
        const localTracks = getLocalTracks(getState()['features/base/tracks']);
        APP.conference.joinRoom(roomName, {
            startWithAudioMuted: isLocalTrackMuted(localTracks, MEDIA_TYPE.AUDIO),
            startWithVideoMuted: isLocalTrackMuted(localTracks, MEDIA_TYPE.VIDEO)
        });
    };
}
/**
 * Joins the room selected while this guest was waiting in the lobby.
 *
 * This is not a normal room switch because the original conference never
 * joined. Tear that conference down without disconnecting, preserve the local
 * mute state, then join the approved room on the existing connection.
 *
 * @param {string} [targetRoomName] - The room selected by the moderator.
 * @returns {Function}
 */
export function joinAdmittedRoom(targetRoomName) {
    return (_dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
        if (!targetRoomName || typeof APP === 'undefined') {
            return;
        }
        const localTracks = getLocalTracks(getState()['features/base/tracks']);
        const startWithAudioMuted = isLocalTrackMuted(localTracks, MEDIA_TYPE.AUDIO);
        const startWithVideoMuted = isLocalTrackMuted(localTracks, MEDIA_TYPE.VIDEO);
        try {
            yield APP.conference.leaveRoom(false /* doDisconnect */, CONFERENCE_LEAVE_REASONS.SWITCH_ROOM);
        }
        catch (error) {
            logger.warn('leaveRoom rejected while joining the admitted room:', error);
        }
        try {
            yield APP.conference.joinRoom(targetRoomName, {
                startWithAudioMuted,
                startWithVideoMuted
            });
        }
        finally {
            APP.conference.autoReconnectSuppressed = false;
        }
    });
}
/**
 * Switches the local participant to another room of the same meeting, keeping
 * the current devices, mute states and virtual background. This is the same
 * leave-without-disconnect + rejoin mechanism the breakout-rooms feature uses;
 * the mtApi switching flags tell the virtual-background middleware to preserve
 * the effect across the transition.
 *
 * @param {string} [targetRoomName] - The room to switch to.
 * @returns {Function}
 */
export function switchToRoom(targetRoomName) {
    return (dispatch, getState) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!targetRoomName) {
            return;
        }
        const toBackstage = isBackstageRoomName(targetRoomName);
        dispatch(setRoomSwitchTarget(toBackstage ? 'backstage' : 'main', (_a = getCurrentConference(getState())) === null || _a === void 0 ? void 0 : _a.getName()));
        dispatch(toBackstage
            ? setIsSwitchingBreakoutRoom(true)
            : setIsSwitchingToMainRoom(true));
        // Mobile has no APP: leave the conference and create the next one
        // straight from the redux connection, the way its breakout-room moves
        // do it, carrying the mute states across.
        if (typeof APP === 'undefined') {
            const conference = getCurrentConference(getState());
            const { audio, video } = getState()['features/base/media'];
            dispatch(conferenceWillLeave(conference));
            try {
                yield (conference === null || conference === void 0 ? void 0 : conference.leave(CONFERENCE_LEAVE_REASONS.SWITCH_ROOM));
            }
            catch (error) {
                logger.warn('MHConference.leave() rejected while switching rooms:', error);
                dispatch(conferenceLeft(conference));
            }
            dispatch(clearNotifications());
            dispatch(createConference(targetRoomName));
            dispatch(setAudioMuted(audio.muted));
            dispatch(setVideoMuted(Boolean(video.muted)));
            dispatch(createDesiredLocalTracks());
            return;
        }
        // Read after the share is stopped: turning it off can restore the camera track,
        // and these values must describe what the participant carries into the new room.
        const localTracks = getLocalTracks(getState()['features/base/tracks']);
        const isAudioMuted = isLocalTrackMuted(localTracks, MEDIA_TYPE.AUDIO);
        const isVideoMuted = isLocalTrackMuted(localTracks, MEDIA_TYPE.VIDEO);
        try {
            yield APP.conference.leaveRoom(false /* doDisconnect */, CONFERENCE_LEAVE_REASONS.SWITCH_ROOM)
                .then(() => APP.API.notifyConferenceLeft(APP.conference.roomName));
        }
        catch (error) {
            logger.warn('leaveRoom rejected while switching rooms:', error);
        }
        APP.conference.joinRoom(targetRoomName, {
            startWithAudioMuted: isAudioMuted,
            startWithVideoMuted: isVideoMuted
        });
    });
}
/**
 * Moves the local participant into the backstage room of the current meeting.
 *
 * @returns {Function}
 */
export function goBackstage() {
    return (dispatch, getState) => {
        if (!amIInBackstage(getState)) {
            dispatch(switchToRoom(getBackstageRoomName(getState)));
        }
    };
}
/**
 * Moves the local participant from backstage onto the stage: back into the main
 * meeting room, visible and audible to everyone.
 *
 * A guest a moderator sent backstage has no say in this: the server holds them
 * there and would refuse the join, so unless the request came from a moderator
 * (relayed by Prosody, see the webinar middleware) it is not even attempted.
 *
 * @param {Object} [options] - Options.
 * @param {boolean} [options.byModerator] - Whether a moderator asked for this
 * move (server-relayed request) rather than the participant themselves.
 * @returns {Function}
 */
export function goLive({ byModerator = false } = {}) {
    return (dispatch, getState) => {
        if (!amIInBackstage(getState)) {
            return;
        }
        if (!byModerator && amIHeldBackstage(getState)) {
            logger.info('Ignoring self-initiated Go Live: held backstage until a moderator brings us live');
            dispatch(showNotification({
                titleKey: 'webinar.heldTitle',
                descriptionKey: 'webinar.heldDescription'
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            return;
        }
        dispatch(switchToRoom(getMainRoomName(getState)));
    };
}
/**
 * Asks the server to move another occupant of the current room between the main
 * room and backstage. Prosody verifies the sender is a moderator and relays a
 * trusted private move request to the target. A moderator can only move people
 * who are in the same room: send main-room panelists backstage from main, bring
 * backstage panelists live from backstage.
 *
 * @param {string} participantId - The target participant id.
 * @param {boolean} live - True to bring them onto the stage (main room), false
 * to send them backstage.
 * @returns {Function}
 */
export function requestParticipantMove(participantId, live) {
    return (_dispatch, getState) => {
        var _a;
        const state = getState();
        // amIWebinarModerator, not the MUC role: in the backstage room an
        // anonymous moderator is reported as a plain participant, and this is
        // the room they send people to backstage FROM.
        if (!participantId || !amIWebinarModerator(state)) {
            return;
        }
        try {
            (_a = getCurrentConference(state)) === null || _a === void 0 ? void 0 : _a.sendMessage({
                name: WEBINAR_MOVE_REQUEST,
                live,
                targetId: participantId
            });
        }
        catch (error) {
            logger.warn(`Failed to request move (live=${live}) for ${participantId}`, error);
        }
    };
}
/**
 * Stores the server-published list of people currently backstage.
 *
 * @param {Array<Object>} occupants - The backstage occupants.
 * @returns {{
 *     type: SET_BACKSTAGE_ROSTER,
 *     occupants: Array<Object>
 * }}
 */
export function setBackstageRoster(occupants) {
    return {
        type: SET_BACKSTAGE_ROSTER,
        occupants
    };
}
/**
 * Asks the server who is currently backstage. Answered privately, and only for
 * moderators of the main room.
 *
 * @returns {Function}
 */
export function requestBackstageRoster() {
    return (_dispatch, getState) => {
        var _a;
        const state = getState();
        if (!isLocalParticipantModerator(state) || amIInBackstage(state)) {
            return;
        }
        try {
            (_a = getCurrentConference(state)) === null || _a === void 0 ? void 0 : _a.sendMessage({
                name: WEBINAR_BACKSTAGE_ROSTER_REQUEST
            });
        }
        catch (error) {
            logger.warn('Failed to request the backstage roster', error);
        }
    };
}
/**
 * Stores the server-published list of people currently in the main room, sent to
 * moderators who are backstage.
 *
 * @param {Array<Object>} occupants - The main-room occupants.
 * @returns {{
 *     type: SET_MAIN_ROOM_ROSTER,
 *     occupants: Array<Object>
 * }}
 */
export function setMainRoomRoster(occupants, policy) {
    return {
        type: SET_MAIN_ROOM_ROSTER,
        occupants,
        policy
    };
}
/**
 * Asks the server who is currently in the main room. Answered privately, and
 * only for moderators who are in the backstage room.
 *
 * @returns {Function}
 */
export function requestMainRoomRoster() {
    return (_dispatch, getState) => {
        var _a;
        const state = getState();
        if (!amIInBackstage(state) || !amIWebinarModerator(state)) {
            return;
        }
        try {
            (_a = getCurrentConference(state)) === null || _a === void 0 ? void 0 : _a.sendMessage({
                name: WEBINAR_MAIN_ROSTER_REQUEST
            });
        }
        catch (error) {
            logger.warn('Failed to request the main-room roster', error);
        }
    };
}
/**
 * Sends a moderator control that Prosody resolves against either room of the
 * meeting, so it can target someone the local client is not in a room with.
 *
 * The local moderator check is a courtesy only — it keeps the pane from sending
 * requests the server would refuse. The server re-checks the sender, and it is
 * the only check that counts.
 *
 * @param {string} name - The control message name.
 * @param {string} participantId - The target participant id.
 * @param {Object} payload - Extra fields for the control.
 * @returns {Function}
 */
function sendCrossRoomControl(name, participantId, payload = {}) {
    return (_dispatch, getState) => {
        var _a;
        const state = getState();
        if (!participantId || !amIWebinarModerator(state)) {
            return;
        }
        try {
            (_a = getCurrentConference(state)) === null || _a === void 0 ? void 0 : _a.sendMessage(Object.assign({ name, targetId: participantId }, payload));
        }
        catch (error) {
            logger.warn(`Failed to send ${name} for ${participantId}`, error);
        }
    };
}
/**
 * Sends a room-wide policy that Prosody applies to the main room, from either
 * room. The per-occupant relay above cannot carry these: they target the room,
 * not a person.
 *
 * @param {string} name - The control message name.
 * @param {Object} payload - The policy fields.
 * @returns {Function}
 */
function sendCrossRoomPolicy(name, payload) {
    return (_dispatch, getState) => {
        var _a;
        const state = getState();
        if (!amIWebinarModerator(state)) {
            return;
        }
        try {
            (_a = getCurrentConference(state)) === null || _a === void 0 ? void 0 : _a.sendMessage(Object.assign({ name }, payload));
        }
        catch (error) {
            logger.warn(`Failed to send ${name}`, error);
        }
    };
}
/**
 * Hides an occupant of either room from the audience, or shows them again.
 *
 * @param {string} participantId - The target participant id.
 * @param {boolean} hidden - True to hide, false to show.
 * @returns {Function}
 */
export function requestParticipantHidden(participantId, hidden) {
    return sendCrossRoomControl(WEBINAR_HIDDEN_REQUEST, participantId, { hidden });
}
/**
 * Grants or revokes a webinar role on an occupant of either room.
 *
 * @param {string} participantId - The target participant id.
 * @param {string} role - 'panelist', 'presenter', or 'participant' to revoke.
 * @returns {Function}
 */
export function requestParticipantRole(participantId, role) {
    return sendCrossRoomControl(WEBINAR_ROLE_REQUEST, participantId, { role });
}
/**
 * Removes an occupant of either room from the meeting.
 *
 * @param {string} participantId - The target participant id.
 * @returns {Function}
 */
export function requestParticipantKick(participantId) {
    return sendCrossRoomControl(WEBINAR_KICK_REQUEST, participantId);
}
/**
 * Asks an occupant of either room to mute a device. Mute only: un-muting
 * somebody else needs their own gesture, so it is not offered.
 *
 * @param {string} participantId - The target participant id.
 * @param {string} mediaType - 'audio' or 'video'.
 * @returns {Function}
 */
export function requestParticipantMute(participantId, mediaType) {
    return sendCrossRoomControl(WEBINAR_MUTE_REQUEST, participantId, { mediaType });
}
/**
 * Asks a main-room stage occupant to move to backstage.
 *
 * @param {string} participantId - The target participant id.
 * @returns {Function}
 */
export function sendToBackstage(participantId) {
    return requestParticipantMove(participantId, false);
}
/**
 * Asks a backstage occupant to go live (move to the main room).
 *
 * @param {string} participantId - The target participant id.
 * @returns {Function}
 */
export function bringLive(participantId) {
    return requestParticipantMove(participantId, true);
}
/**
 * Asks the server to bring every backstage stage-occupant live. Unlike
 * requestParticipantMove (a same-room server request), this crosses rooms:
 * prosody verifies the sender is a moderator of the main room, suppresses the
 * trigger message, and relays a moderator-stamped move request to each occupant
 * of the '-backstage' room.
 *
 * @returns {Function}
 */
export function bringAllBackstageLive() {
    return (dispatch, getState) => {
        var _a;
        const state = getState();
        if (!isLocalParticipantModerator(state) || amIInBackstage(state)) {
            return;
        }
        try {
            (_a = getCurrentConference(state)) === null || _a === void 0 ? void 0 : _a.sendMessage({
                name: WEBINAR_BRING_ALL_LIVE
            });
            // The backstage room's occupancy is invisible from the main room, so
            // this is fire-and-forget; without feedback the button feels dead.
            dispatch(showNotification({
                titleKey: 'webinar.bringAllRequestedTitle',
                descriptionKey: 'webinar.bringAllRequestedDescription'
            }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
        }
        catch (error) {
            logger.warn('Failed to request bring-all-live', error);
        }
    };
}
/**
 * Records the room's confirmed local-recording policy as read from room
 * metadata. Server truth only — dispatched from the metadata broadcast.
 *
 * @param {boolean} [allowed] - The confirmed {@code localRecording.allowed}
 * value, or undefined when the room has no explicit policy (recording is then
 * allowed, the pre-policy behavior).
 * @returns {{
 *     type: SET_LOCAL_RECORDING_ALLOWED,
 *     allowed: boolean | undefined
 * }}
 */
export function setLocalRecordingAllowedState(allowed) {
    return {
        type: SET_LOCAL_RECORDING_ALLOWED,
        allowed
    };
}
/**
 * Records the room's "block everyone's mic and camera" flag as read from room
 * metadata.
 *
 * @param {boolean|undefined} enabled - The flag, or undefined when unset.
 * @returns {{
 *     type: SET_MEDIA_BLOCK_ALL,
 *     enabled: boolean | undefined
 * }}
 */
export function setMediaBlockAllState(enabled) {
    return {
        type: SET_MEDIA_BLOCK_ALL,
        enabled
    };
}
/**
 * Turns "block everyone's mic and camera" on or off for the room. Moderator
 * only. Lives in room metadata so that it reaches everyone in the room --
 * including whoever joins later, and a visitor who is promoted -- from the
 * room itself, rather than from a moderator's client that may be backstage.
 * Each non-moderator client mutes itself and locks its own buttons on seeing
 * it (see the webinar middleware).
 *
 * @param {boolean} enabled - Whether everyone but moderators is blocked.
 * @returns {Function}
 */
export function setMediaBlockAllPolicy(enabled) {
    return (dispatch, getState) => {
        var _a, _b, _c;
        const state = getState();
        if (!isLocalParticipantModerator(state)) {
            return;
        }
        // Backstage is its own room; Prosody writes the main room's metadata.
        if (amIInBackstage(state)) {
            dispatch(sendCrossRoomPolicy(WEBINAR_BLOCK_ALL_REQUEST, { enabled }));
            return;
        }
        const metadataHandler = (_b = (_a = getCurrentConference(state)) === null || _a === void 0 ? void 0 : _a.getMetadataHandler) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (!((_c = metadataHandler === null || metadataHandler === void 0 ? void 0 : metadataHandler.isSupported) === null || _c === void 0 ? void 0 : _c.call(metadataHandler))) {
            logger.warn('setMediaBlockAllPolicy: room metadata not available');
            return;
        }
        try {
            metadataHandler.setMetadata(MEDIA_BLOCK_ALL_METADATA_KEY, {
                enabled,
                updatedAt: Date.now()
            });
        }
        catch (error) {
            logger.warn('setMediaBlockAllPolicy: setMetadata failed', error);
        }
    };
}
/**
 * Moderator action that allows or blocks local recording for the room,
 * persisted as room metadata {@code localRecording.allowed} so every client
 * (including late joiners) enforces the same policy. The local flag is only
 * updated once the resulting METADATA_UPDATED broadcast comes back.
 *
 * @param {boolean} allowed - Whether local recording is allowed in this room.
 * @returns {Function}
 */
export function setLocalRecordingPolicy(allowed) {
    return (dispatch, getState) => {
        var _a, _b, _c;
        const state = getState();
        if (!isLocalParticipantModerator(state)) {
            return;
        }
        // Backstage is its own room: writing its metadata would change nothing
        // for the audience, and the metadata component refuses a write to the
        // main room from a non-occupant. Prosody applies it there instead.
        if (amIInBackstage(state)) {
            dispatch(sendCrossRoomPolicy(WEBINAR_LOCAL_RECORDING_REQUEST, { allowed }));
            return;
        }
        const metadataHandler = (_b = (_a = getCurrentConference(state)) === null || _a === void 0 ? void 0 : _a.getMetadataHandler) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (!((_c = metadataHandler === null || metadataHandler === void 0 ? void 0 : metadataHandler.isSupported) === null || _c === void 0 ? void 0 : _c.call(metadataHandler))) {
            logger.warn('setLocalRecordingPolicy: room metadata not available');
            return;
        }
        try {
            metadataHandler.setMetadata(LOCAL_RECORDING_METADATA_KEY, {
                allowed,
                updatedAt: Date.now()
            });
        }
        catch (error) {
            logger.warn('setLocalRecordingPolicy: setMetadata failed', error);
        }
    };
}
